import { useState } from "react";
import type { ReactNode } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Check, MonitorCheck, X } from "lucide-react";
import { approveTrustOverride, cancelTrustOverride, fetchTrustOverrides } from "../../api/trustOverrides.js";
import type { TrustOverride } from "../../api/trustOverrides.js";
import { describeAuthError } from "../../app/authFlow.js";
import { ADMIN_AUDIT_KEY } from "../../data/account.js";
import { formatDateTime, formatRelativeTime } from "../../lib/format.js";
import { Alert, Badge, Button, Card, CardHeader } from "../../ui/primitives.js";
import { useToast } from "../../ui/Toast.js";

export const TRUST_OVERRIDES_KEY: ReadonlyArray<string> = ["admin", "trust-overrides"];

type Act = "approve" | "cancel";

/**
 * Admin: open "trust next device" overrides – a second admin approves, any admin cancels. Hidden
 * while none is open.
 */
export function TrustOverridesPanel(props: { readonly selfId: string }): ReactNode {
  const queryClient = useQueryClient();
  const toast = useToast();
  const overrides = useQuery({ queryKey: TRUST_OVERRIDES_KEY, queryFn: fetchTrustOverrides });
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  async function act(override: TrustOverride, kind: Act): Promise<void> {
    setBusy(`${override.id}:${kind}`);
    setError(null);
    try {
      if (kind === "approve") {
        await approveTrustOverride(override.id);
        toast.notify(`Nächstes Gerät von ${override.email ?? "dem Konto"} freigegeben.`, { tone: "success" });
      } else {
        await cancelTrustOverride(override.id);
        toast.notify("Gerätefreigabe abgebrochen.", { tone: "success" });
      }
      void queryClient.invalidateQueries({ queryKey: TRUST_OVERRIDES_KEY });
      void queryClient.invalidateQueries({ queryKey: ADMIN_AUDIT_KEY });
    } catch (err: unknown) {
      setError(describeAuthError(err));
    } finally {
      setBusy(null);
    }
  }

  if (overrides.data !== undefined && overrides.data.length === 0 && error === null) {
    return null;
  }
  return (
    <Card>
      <CardHeader
        icon={MonitorCheck}
        title="Gerätefreigaben"
        description="Für Konten, die alle freigegebenen Geräte verloren haben. Ein zweiter Administrator muss zustimmen; danach gilt die nächste Anmeldung auf einem neuen Gerät einmalig als freigegeben, höchstens 24 Stunden lang."
      />
      <div className="flex flex-col gap-3 px-5 py-4">
        {error !== null ? <Alert tone="danger">{error}</Alert> : null}
        {overrides.error !== null ? <Alert tone="danger">{overrides.error.message}</Alert> : null}
        {overrides.data !== undefined && overrides.data.length > 0 ? (
          <ul className="divide-y divide-line">
            {overrides.data.map((override: TrustOverride): ReactNode => {
              const approved = override.status === "approved";
              const canApprove = !approved && override.requestedBy !== props.selfId && override.userId !== props.selfId;
              return (
                <li key={override.id} className="flex flex-col gap-2 py-3 sm:flex-row sm:items-center sm:justify-between">
                  <div className="min-w-0 text-sm">
                    <p className="truncate font-medium text-foam">{override.email ?? override.userId}</p>
                    <p className="text-xs text-mist-2">
                      von {override.requestedByEmail ?? "unbekannt"}
                      {approved ? ` · bestätigt von ${override.approvedByEmail ?? "unbekannt"}` : " · wartet auf Zustimmung"}
                    </p>
                    <p className="text-xs text-mist-2" title={formatDateTime(override.expiresAt)}>
                      {approved ? <Badge tone="mint">Bereit für die Anmeldung</Badge> : null} Läuft {formatRelativeTime(override.expiresAt, Date.now())} ab
                    </p>
                  </div>
                  <div className="flex gap-2">
                    {canApprove ? (
                      <Button size="sm" variant="secondary" icon={Check} loading={busy === `${override.id}:approve`} onClick={(): void => void act(override, "approve")}>
                        Zustimmen
                      </Button>
                    ) : null}
                    <Button size="sm" variant="ghost" icon={X} loading={busy === `${override.id}:cancel`} onClick={(): void => void act(override, "cancel")}>
                      Abbrechen
                    </Button>
                  </div>
                </li>
              );
            })}
          </ul>
        ) : null}
      </div>
    </Card>
  );
}
